import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import TablePagination from "../../commons/Pagination/TablePagination";
import DateRangeSelectButton from "../../commons/DateRangeSelectButton/DateRangeSelectButton";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import CopyableId from "../../commons/CopyableId/CopyableId";
import viewImage from "../../assets/images/icons/view.svg";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";

const NOTE_TYPE_OPTIONS = ["All", "Credit", "Debit"];
const USER_TYPE_OPTIONS = ["All", "Restaurant", "Driver", "Both"];

const NOTE_TYPE_MAP = {
  Credit: "credit",
  Debit: "debit",
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatLabel = (value) => {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatStatus = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const mapNoteRow = (item, index) => ({
  id: item?._id || `${item?.cr_dr_note_id || "note"}-${index}`,
  noteId: item?.cr_dr_note_id || "-",
  createdAt: item?.createdAt || item?.created_at || null,
  orderId: typeof item?.order_id === "object" ? item?.order_id?._id : item?.order_id,
  noteType: formatLabel(item?.cr_dr_note_type),
  userType: formatLabel(item?.user_type),
  restaurantAmount: formatMoney(item?.restaurant_amount),
  driverAmount: formatMoney(item?.driver_amount),
  sumOfAmount: formatMoney(item?.sum_of_amount),
  restaurantSettlement: formatStatus(item?.restaurant_sattlement_status_str),
  driverSettlement: formatStatus(item?.driver_sattlement_status_str),
  description: item?.description || "-",
});

const parseNoteList = (payload) => {
  if (Array.isArray(payload)) return payload.map(mapNoteRow);
  const list = payload?.payout_cr_dr_notes;
  return Array.isArray(list) ? list.map(mapNoteRow) : [];
};

function PayoutCrDrNotes() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [appliedOrderId, setAppliedOrderId] = useState("");
  const [noteTypeFilter, setNoteTypeFilter] = useState("All");
  const [userTypeFilter, setUserTypeFilter] = useState("All");
  const [showNoteTypeFilter, setShowNoteTypeFilter] = useState(false);
  const [showUserTypeFilter, setShowUserTypeFilter] = useState(false);
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [showAppliedRange, setShowAppliedRange] = useState(false);
  const noteTypeFilterRef = useRef(null);
  const userTypeFilterRef = useRef(null);
  const limit = 10;

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (NOTE_TYPE_MAP[noteTypeFilter]) {
        params.set("cr_dr_note_type", NOTE_TYPE_MAP[noteTypeFilter]);
      }
      if (userTypeFilter && userTypeFilter !== "All") {
        params.set("user_type", userTypeFilter);
      }
      if (appliedOrderId.trim()) {
        params.set("order_id", appliedOrderId.trim());
      }
      if (fromdate) {
        params.set("fromdate", fromdate);
      }
      if (todate) {
        params.set("todate", todate);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.listPayoutCrDrNote}?${params.toString()}`
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        const payload = response?.data?.data;
        setRows(parseNoteList(payload));
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              payload?.totalPages ||
              payload?.total_pages ||
              1
          ) || 1
        );
      } else {
        setRows([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching payout CR/DR notes:", error);
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, limit, noteTypeFilter, userTypeFilter, appliedOrderId, fromdate, todate]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        noteTypeFilterRef.current &&
        !noteTypeFilterRef.current.contains(event.target)
      ) {
        setShowNoteTypeFilter(false);
      }
      if (
        userTypeFilterRef.current &&
        !userTypeFilterRef.current.contains(event.target)
      ) {
        setShowUserTypeFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedOrderId(orderId.trim());
    setPage(1);
  };

  const handleApplyDateRange = (start, end) => {
    setFromdate(start);
    setTodate(end);
    setShowAppliedRange(true);
    setPage(1);
  };

  const handleResetDateRange = () => {
    setFromdate(null);
    setTodate(null);
    setShowAppliedRange(false);
    setPage(1);
  };

  const handleViewNote = (item) => {
    if (!item?.id) return;
    navigate(`/payout-management/credit-debit/${item.id}`);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">
                Payout Credit &amp; Debit
              </h3>
            </div>

            {showAppliedRange && fromdate && todate && (
              <p className="text-muted small mb-3">
                Showing data from {fromdate} to {todate}
              </p>
            )}

            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0 flex-wrap">
                  <form className="col-md-4 px-0" onSubmit={handleSearchSubmit}>
                    <div className="form-group my-0">
                      <input
                        className="form-control search-icon left-icon border"
                        placeholder="Search by Order ID..."
                        value={orderId}
                        onChange={(e) => setOrderId(e.target.value)}
                      />
                    </div>
                  </form>

                  <div className="d-flex align-items-center ml-auto mt-2 mt-md-0 flex-nowrap">
                    <div
                      ref={noteTypeFilterRef}
                      className="d-flex align-items-center position-relative mr-3"
                    >
                      <FilterTrigger
                        active={noteTypeFilter !== "All"}
                        onClick={() => {
                          setShowNoteTypeFilter((prev) => !prev);
                          setShowUserTypeFilter(false);
                        }}
                      >
                        {showNoteTypeFilter && (
                          <FilterOptionsMenu
                            options={NOTE_TYPE_OPTIONS}
                            value={noteTypeFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setNoteTypeFilter(option);
                              setShowNoteTypeFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowNoteTypeFilter((prev) => !prev);
                          setShowUserTypeFilter(false);
                        }}
                      >
                        {noteTypeFilter === "All" ? "All notes" : noteTypeFilter}
                      </span>
                    </div>

                    <div
                      ref={userTypeFilterRef}
                      className="d-flex align-items-center position-relative mr-3"
                    >
                      <FilterTrigger
                        active={userTypeFilter !== "All"}
                        onClick={() => {
                          setShowUserTypeFilter((prev) => !prev);
                          setShowNoteTypeFilter(false);
                        }}
                      >
                        {showUserTypeFilter && (
                          <FilterOptionsMenu
                            options={USER_TYPE_OPTIONS}
                            value={userTypeFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setUserTypeFilter(option);
                              setShowUserTypeFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowUserTypeFilter((prev) => !prev);
                          setShowNoteTypeFilter(false);
                        }}
                      >
                        {userTypeFilter === "All" ? "All users" : userTypeFilter}
                      </span>
                    </div>

                    <DateRangeSelectButton
                      appliedFrom={fromdate}
                      appliedTo={todate}
                      onApply={handleApplyDateRange}
                      onReset={handleResetDateRange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th className="text-left">Date</th>
                        <th className="text-left">Note ID</th>
                        <th className="text-left">Order ID</th>
                        <th className="text-left">Note Type</th>
                        <th className="text-left">User Type</th>
                        <th className="text-right">Restaurant Amount</th>
                        <th className="text-right">Driver Amount</th>
                        <th className="text-right">Sum</th>
                        <th className="text-left">Restaurant Settlement</th>
                        <th className="text-left">Driver Settlement</th>
                        <th className="text-left">Description</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">
                                {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : rows.length > 0 ? (
                        rows.map((item) => (
                          <tr key={item.id}>
                            <td className="text-left">
                              {formatDateTime(item.createdAt)}
                            </td>
                            <td className="text-left">
                              {item.noteId !== "-" ? (
                                <CopyableId value={item.noteId} />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-left">
                              {item.orderId ? (
                                <CopyableId value={item.orderId} />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-left">{item.noteType}</td>
                            <td className="text-left">{item.userType}</td>
                            <td className="text-right">{item.restaurantAmount}</td>
                            <td className="text-right">{item.driverAmount}</td>
                            <td className="text-right">{item.sumOfAmount}</td>
                            <td className="text-left">{item.restaurantSettlement}</td>
                            <td className="text-left">{item.driverSettlement}</td>
                            <td className="text-left">{item.description}</td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mx-2"
                                  title="View"
                                  onClick={() => handleViewNote(item)}
                                >
                                  <img src={viewImage} alt="View" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayoutCrDrNotes;
